import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { NAVIGATION } from '@/app/navigation'
import { ApiError } from '@/lib/api'
import { fetchColumns } from '@/lists/api'
import { AccessDenied } from '@/pages/AccessDenied'
import { NotFound } from '@/pages/NotFound'
import { fetchRecord, isConflict, saveRecord, supportsOptimisticLocking } from './api'
import { CardForm } from './CardForm'
import { ConflictPanel } from './ConflictPanel'
import { StateView } from './record/StateView'
import { cardDescriptor } from './descriptors'
import { changedFields, conflictOf, initialValues, validate, visibleColumns, visibleSections } from './form'
import { standardTabs } from './tabs'
import type { CardConflict, CardDescriptor, FieldError, FormValues } from './types'

/**
 * Карточка одной записи (тикет #66, ТЗ §5.1).
 *
 * Один компонент на все сущности — по той же причине, по которой один компонент на все
 * списки (#48): состав секций приходит из описания, колонки и полевые права — с сервера, и
 * карточка контрагента отличается от карточки сделки только данными. Отдельный экран на
 * каждую означал бы, что оптимистичную блокировку и полевые права надо написать заново
 * столько раз, сколько в системе сущностей.
 *
 * ## Что здесь честно не сделано
 *
 * Оптимистичная блокировка реализована целиком — сравнение версий, конфликт с обеими
 * сторонами на экране, решение по каждому полю, — но **источник версии пуст**: `version`
 * не объявлен в каталоге списков сервера, а выделенного REST карточки ещё нет (#67, #68).
 * Пока это так, карточка показывает предупреждение вместо того, чтобы обещать защиту,
 * которой нет. Как только сервер начнёт отдавать версию, предупреждение исчезнет само —
 * см. `supportsOptimisticLocking`.
 */

interface EntityCardPageProps {
  /** Код сущности; берётся из маршрута, но передаётся явно ради тестируемости. */
  entity?: string
}

export function EntityCardPage({ entity: entityProp }: EntityCardPageProps = {}) {
  const params = useParams<{ entity?: string; id?: string }>()
  const entity = entityProp ?? params.entity
  const id = params.id
  const descriptor = entity ? cardDescriptor(entity) : null

  if (!entity || !id || !descriptor) {
    // Сущность без описания карточки — это не «пустая карточка», а её отсутствие.
    // Собрать её из колонок каталога значило бы выдать экран из `stage_id` и
    // `department_id` за спроектированный.
    return <NotFound />
  }

  return <CardBody descriptor={descriptor} recordId={id} />
}

function CardBody({ descriptor, recordId }: { descriptor: CardDescriptor; recordId: string }) {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState<FormValues>({})
  const [errors, setErrors] = useState<FieldError[]>([])
  const [conflict, setConflict] = useState<CardConflict | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('main')

  const columnsQuery = useQuery({
    queryKey: ['list-columns', descriptor.entity],
    queryFn: ({ signal }) => fetchColumns(descriptor.entity, signal),
    // Тот же ключ, что у списка: каталог колонок один и тот же, и второй запрос за ним
    // при переходе из списка в карточку был бы лишним кругом.
    staleTime: 30 * 60 * 1000,
    retry: false,
  })

  const allColumns = useMemo(() => columnsQuery.data?.columns ?? [], [columnsQuery.data])

  const recordQuery = useQuery({
    queryKey: ['record', descriptor.entity, recordId],
    enabled: allColumns.length > 0,
    queryFn: ({ signal }) => fetchRecord(descriptor.entity, recordId, allColumns, signal),
    retry: false,
  })

  const record = recordQuery.data ?? null

  // Видимые поля считаются из описания колонок сервера: колонку, на которую нет полевого
  // права, он не описывает вовсе, и пересечение с секциями — единственный источник правды
  // о том, что показывать.
  const visible = useMemo(
    () => visibleColumns(descriptor.sections, allColumns),
    [descriptor.sections, allColumns],
  )
  const sections = useMemo(
    () => visibleSections(descriptor.sections, allColumns),
    [descriptor.sections, allColumns],
  )

  const beginEdit = useCallback(() => {
    if (!record) return
    setValues(initialValues(record, visible))
    setErrors([])
    setConflict(null)
    setEditing(true)
  }, [record, visible])

  const cancelEdit = useCallback(() => {
    setEditing(false)
    setValues({})
    setErrors([])
    setConflict(null)
  }, [])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!record) throw new Error('card.noRecord')
      const changes = changedFields(record, values, visible)
      if (Object.keys(changes).length === 0) return { applied: [], failure: null }

      // Перечитывание перед записью — не гонка, а её обнаружение. Настоящую защиту даёт
      // сервер по `version`; здесь мы ловим расхождение, которое успело произойти, и
      // показываем его вместо того, чтобы записать поверх.
      const current = await fetchRecord(descriptor.entity, recordId, allColumns)
      if (!current) throw new Error('card.gone')

      const drift = conflictOf(current, changes, record)
      if (drift.fields.length > 0) {
        setConflict(drift)
        // Бросаем, а не возвращаем: мутация обязана считаться неуспешной, иначе карточка
        // выйдет из режима правки и объявит сохранённым то, что не сохранялось.
        throw new ConflictSignal()
      }

      return (descriptor.save ?? saveRecord)(descriptor.entity, recordId, changes, record.version)
    },
    onSuccess: (outcome) => {
      if (outcome.failure) {
        // Отказ по версии приходит СЮДА, а не в `onError`: `saveRecord` ловит ошибку
        // поля и возвращает её отчётом, чтобы назвать, что успело записаться. Не разобрав
        // её здесь, конфликт показали бы как «сохранено частично» — то есть сообщением,
        // из которого не следует, что чужая правка цела и решать её надо человеку.
        if (isConflict(outcome.failure.error)) {
          setNotice(t('card.conflict.detected'))
          void queryClient.invalidateQueries({
            queryKey: ['record', descriptor.entity, recordId],
          })
          return
        }

        // Частичное сохранение: `bulk/update` пишет по колонке за раз, и падение на
        // третьем поле оставляет первые два записанными. Молчать об этом значит показать
        // карточку, состояние которой пользователь считает своим, а оно чужое.
        setNotice(
          t('card.savedPartially', {
            applied: outcome.applied.length,
            field: t(`column.${outcome.failure.field}`),
            reason: messageOf(outcome.failure.error, t),
          }),
        )
      } else {
        setNotice(t('card.saved'))
        setEditing(false)
      }
      void queryClient.invalidateQueries({ queryKey: ['record', descriptor.entity, recordId] })
      // Список тоже устарел: строка, из которой пришли, показывает старое значение.
      void queryClient.invalidateQueries({ queryKey: ['list', descriptor.entity] })
    },
    onError: (error) => {
      if (error instanceof ConflictSignal) return
      if (isConflict(error)) {
        setNotice(t('card.conflict.detected'))
        void queryClient.invalidateQueries({ queryKey: ['record', descriptor.entity, recordId] })
        return
      }
      setNotice(messageOf(error, t))
    },
  })

  const submit = useCallback(() => {
    setNotice(null)
    const found = validate(values, visible)
    setErrors(found)
    // Отправка с известными ошибками — это круг к серверу ради отказа, который уже
    // посчитан здесь. Сервер всё равно проверит: клиентская проверка удобство, не защита.
    if (found.length === 0) saveMutation.mutate()
  }, [saveMutation, values, visible])

  const resolveTakeTheirs = useCallback(
    (field: string) => {
      if (!conflict) return
      setValues((previous) => ({ ...previous, [field]: conflict.theirs.values[field] }))
      setConflict((previous) =>
        previous ? { ...previous, fields: previous.fields.filter((f) => f !== field) } : null,
      )
    },
    [conflict],
  )

  const resolveKeepMine = useCallback((field: string) => {
    setConflict((previous) =>
      previous ? { ...previous, fields: previous.fields.filter((f) => f !== field) } : null,
    )
  }, [])

  const discardEdit = useCallback(() => {
    setConflict(null)
    cancelEdit()
    void queryClient.invalidateQueries({ queryKey: ['record', descriptor.entity, recordId] })
  }, [cancelEdit, descriptor.entity, queryClient, recordId])

  // ------------------------------------------------------------------ отрисовка

  const mayRead = can(user, descriptor.resource, 'read')
  // Право на сущность И возможность править поля: у счёта итоги вычисляются доменом, и
  // сервер не принимает запись ни в одну его колонку (`importable` пуст). Кнопка правки
  // там вела бы к гарантированному отказу — а кнопка, которая всегда ошибается, учит
  // игнорировать ошибки. Запрет при этом остаётся серверным, здесь только честный экран.
  const mayUpdate =
    can(user, descriptor.resource, 'update') &&
    !descriptor.readOnlyFields &&
    (!descriptor.editable || !record || descriptor.editable(record.values))

  // Проверка на маршруте, а не только в меню: прямая ссылка минует навигацию. Данные
  // пользователь всё равно не получит — сервер откажет, — но бесконечная загрузка с 403
  // в консоли выглядит как поломка, а не как запрет.
  if (!mayRead) return <AccessDenied />

  if (columnsQuery.isLoading || recordQuery.isLoading) return <p>{t('app.loading')}</p>

  const failure = columnsQuery.error ?? recordQuery.error
  if (failure && descriptor.view) {
    // The design's states name the kind of emptiness: "no access" and "error" ask for
    // different actions, and one red line for both sends people to support with a working
    // denial.
    const forbidden = failure instanceof ApiError && failure.isForbidden
    return (
      <StateView
        boxed
        kind={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('record.state.forbidden.title') : t('record.state.error.title')}
        description={forbidden ? t('record.state.forbidden.text') : t('record.state.error.text')}
        actions={
          <>
            {!forbidden && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  void columnsQuery.refetch()
                  void recordQuery.refetch()
                }}
              >
                {t('record.state.retry')}
              </button>
            )}
            <Link to={listPathOf(descriptor.entity)} className="btn btn-ghost btn-sm">
              {t('record.state.toList')}
            </Link>
          </>
        }
        code={failure instanceof ApiError ? String(failure.status) : undefined}
      />
    )
  }
  if (failure) {
    return (
      <p role="alert">
        {failure instanceof ApiError && failure.isForbidden ? t('access.denied') : t('app.error')}
      </p>
    )
  }

  // Записи нет: удалена или не попала в область видимости. Различить эти два случая клиент
  // не может, и оба означают «этой записи для вас нет».
  if (!record) return <NotFound />

  const tabs = standardTabs(descriptor.extraTabs)
  const context = {
    entity: descriptor.entity,
    resource: descriptor.resource,
    recordId,
    record,
    columns: allColumns,
    editing,
  }

  const form = (
    <CardForm
      entity={descriptor.entity}
      sections={sections}
      columns={visible}
      values={editing ? values : record.values}
      errors={errors}
      editing={editing}
      readOnlyColumns={descriptor.readOnlyColumns}
      onChange={(field, value) => setValues((previous) => ({ ...previous, [field]: value }))}
    />
  )

  if (descriptor.view) {
    const View = descriptor.view
    return (
      <View
        descriptor={descriptor}
        recordId={recordId}
        record={record}
        columns={allColumns}
        context={context}
        form={form}
        notices={
          <>
            {editing && !supportsOptimisticLocking(record) && (
              <p className="notice" role="status">
                {t('card.noVersion')}
              </p>
            )}
            {notice && (
              <p className="notice" role="status">
                {notice}
              </p>
            )}
            {conflict && (
              <ConflictPanel
                conflict={conflict}
                onTakeTheirs={resolveTakeTheirs}
                onKeepMine={resolveKeepMine}
                onDiscard={discardEdit}
              />
            )}
          </>
        }
        edit={{
          editing,
          mayUpdate,
          saving: saveMutation.isPending,
          begin: beginEdit,
          submit,
          cancel: cancelEdit,
        }}
      />
    )
  }

  return (
    <section className="entity-card">
      <header className="card-header">
        <div>
          <Link to={listPathOf(descriptor.entity)} className="card-back">
            ← {t(descriptor.titleKey)}
          </Link>
          <h1>{cardTitle(descriptor, record.values, recordId)}</h1>
        </div>

        <div className="card-actions">
          {!editing && mayUpdate && (
            <button type="button" onClick={beginEdit}>
              {t('card.edit')}
            </button>
          )}
          {editing && (
            <>
              <button type="button" onClick={submit} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? t('card.saving') : t('card.save')}
              </button>
              <button type="button" onClick={cancelEdit} disabled={saveMutation.isPending}>
                {t('card.cancel')}
              </button>
            </>
          )}
        </div>
      </header>

      {/* Пробел называется вслух, а не прячется: обещать защиту от параллельной правки,
          не имея версии, значит дать пользователю уверенность, которой нечем подкрепить. */}
      {editing && !supportsOptimisticLocking(record) && (
        <p className="notice" role="status">
          {t('card.noVersion')}
        </p>
      )}

      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}

      {conflict && (
        <ConflictPanel
          conflict={conflict}
          onTakeTheirs={resolveTakeTheirs}
          onKeepMine={resolveKeepMine}
          onDiscard={discardEdit}
        />
      )}

      <nav className="card-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'main'}
          onClick={() => setActiveTab('main')}
        >
          {t('card.tab.main')}
        </button>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </nav>

      <div className="card-tab-body" role="tabpanel">
        {activeTab === 'main' ? (
          form
        ) : (
          tabs.find((tab) => tab.id === activeTab)?.render(context)
        )}
      </div>

      <footer className="card-footer">
        <button type="button" onClick={() => navigate(-1)}>
          {t('card.back')}
        </button>
      </footer>
    </section>
  )
}

/**
 * Сигнал «обнаружен конфликт».
 *
 * Не `Error` с текстом, а свой класс: конфликт — не поломка, и обработчик ошибки обязан
 * отличать его от отказа сервера, чтобы не показать поверх панели разрешения ещё и
 * красное сообщение о сбое.
 */
class ConflictSignal extends Error {
  constructor() {
    super('conflict')
    this.name = 'ConflictSignal'
  }
}

/**
 * Заголовок карточки.
 *
 * Первая непустая колонка из объявленных, иначе идентификатор. Пустая строка вместо
 * заголовка выглядит как незагрузившийся экран, а идентификатор хотя бы говорит, что
 * запись открыта — и его можно назвать в поддержке.
 */
export function cardTitle(
  descriptor: CardDescriptor,
  values: Record<string, unknown>,
  fallbackId: string,
): string {
  const parts = descriptor.titleColumns
    .map((column) => values[column])
    .filter((value) => value != null && String(value).trim() !== '')
    .map(String)
  if (parts.length > 0) {
    return descriptor.titleJoined ? parts.join(' · ') : parts[0]!
  }
  return fallbackId
}

/**
 * Путь списка, из которого открыта карточка.
 *
 * Берётся из навигации, а не собирается из кода сущности: `sales.counterparty` даёт
 * `/counterparties`, а не `/counterpartys`, и правило множественного числа, выведенное
 * из строки, ошибётся на первой же сущности с нестандартной формой — причём ошибётся
 * ссылкой, которая выглядит рабочей до нажатия.
 */
export function listPathOf(entity: string): string {
  const item = NAVIGATION.flatMap((section) => section.items).find(
    (candidate) => candidate.listEntity === entity,
  )
  return item?.path ?? '/'
}

/** Человеческий текст ошибки; `ApiError` уже несёт разобранное сообщение сервера. */
function messageOf(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return t(error.message)
  return String(error)
}
